import { resolveOrganizationBrandName } from './branding';
import d28dPlatform from '../../shared/d28dPlatformConstants.json';

const D28D_ANCHOR_INVITE = String(d28dPlatform.anchorInvite || 'GYM-D28D-004').toUpperCase();
const D28D_ANCHOR_SLUG = String(d28dPlatform.anchorSlug || 'd28d-marca-blanca').toLowerCase();
const D28D_SEED_INVITE = String(d28dPlatform.seedInvite || 'D28D-SEED').toUpperCase();
const D28D_SEED_SLUG = String(d28dPlatform.seedSlug || 'd28d-seed-template').toLowerCase();

const MODULE_LABELS = {
  d28d: 'D28D',
  gym: 'D28D',
  live_classes: 'D28D',
  food: 'FOOD',
  training: 'Training',
};

/** Etiqueta de tipo visible (sin tenant/company). */
export function orgTypeLabel({ tenantType, gym, isSede = false }) {
  const type = String(tenantType || '').toLowerCase();
  if (type === 'trainer') return 'Coach';
  if (type === 'gym' || gym) {
    if (isSede || gym?.parent_gym_id) return 'Sede';
    return 'Gimnasio virtual';
  }
  if (type === 'company' || type === 'organization') return 'Empresa Wellness';
  return 'Organización';
}

export function orgDisplayName(entity, tenantRow) {
  return resolveOrganizationBrandName({
    entity,
    tenantRow,
    platformName: '—',
  });
}

/** Licencias activas del operador — varias simultáneas (D28D, FOOD, Training). */
export function collectOrgLicenses(operator) {
  if (!operator) return [];
  const seen = new Set();
  const chips = [];
  const rows = Array.isArray(operator.licenses) ? operator.licenses : [];
  for (const lic of rows) {
    if (lic.active === false) continue;
    const code = String(lic.module_code || lic.moduleCode || '').toLowerCase();
    const label = MODULE_LABELS[code] || code.toUpperCase();
    if (!code || seen.has(label)) continue;
    seen.add(label);
    chips.push({ code, label, source: 'license' });
  }
  if (!chips.length && operator.module_access && typeof operator.module_access === 'object') {
    for (const [key, val] of Object.entries(operator.module_access)) {
      if (val !== true) continue;
      const code = key === 'food_plan' || key === 'nutrition' ? 'food' : key;
      if (MODULE_LABELS[code] === 'D28D') continue;
      const label = MODULE_LABELS[code] || String(code).toUpperCase();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      chips.push({ code, label, source: 'module_access' });
    }
  }
  const sub = operator.subscription;
  if (sub?.has_account) {
    const adminLabel = 'Admin';
    if (!seen.has(adminLabel)) {
      seen.add(adminLabel);
      chips.push({
        code: 'admin',
        label: adminLabel,
        plan: sub.plan,
        estado: sub.estado,
        source: 'subscription',
      });
    }
  }
  return chips;
}

export function isGymActive(gym) {
  return Boolean(gym && gym.activo !== false && String(gym.status || 'active').toLowerCase() !== 'inactive');
}

/** Evita duplicados en listas tras merge API (p. ej. GET /gyms + GET /gyms/:id). */
export function dedupeGymsById(gyms = []) {
  const seen = new Set();
  return gyms.filter((g) => {
    const id = Number(g?.id);
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Etiqueta única para selects (nombre + código; evita dos filas «D28D» por brand_name). */
export function gymSelectOptionLabel(gym, t = null) {
  if (!gym) return '—';
  // Ancla operativa: una sola etiqueta "D28D" (no "Marca Blanca" + "Plataforma").
  if (isD28dPlatformGym(gym)) {
    return typeof t === 'function' ? t('gyms.kind_d28d', 'D28D') : 'D28D';
  }
  // Plantilla SEED: no debería aparecer en filtros operativos; si llega, marcar interno.
  if (isPlatformTemplateGym(gym)) {
    const nombre = String(gym.nombre || '').trim() || 'Plantilla';
    return `${nombre} [interno]`;
  }
  const nombre = String(gym.nombre || '').trim();
  const invite = String(gym.invite_code || gym.inviteCode || '').trim();
  const ciudad = String(gym.ciudad || '').trim();
  let label = nombre || String(gym.brand_name || '').trim() || `Gym #${gym.id}`;
  const parts = [];
  if (invite && !label.includes(invite)) parts.push(invite);
  if (ciudad && !label.toLowerCase().includes(ciudad.toLowerCase())) parts.push(ciudad);
  if (parts.length) label = `${label} · ${parts.join(' · ')}`;
  return label;
}

/**
 * Opciones de filtro operativo D28D (Reportes / Seguimiento).
 * Excluye plantilla SEED y el gym ancla (cubierto por la opción sintética "D28D").
 */
export function gymOptionsForD28dScopeFilter(gyms = []) {
  return dedupeGymsById(gyms)
    .filter((g) => isGymActive(g) && !isPlatformTemplateGym(g) && !isD28dPlatformGym(g))
    .map((g) => ({
      id: Number(g.id),
      nombre: g.nombre || g.name || `Gimnasio ${g.id}`,
      parent_gym_id: g.parent_gym_id ?? g.parentGymId ?? null,
    }))
    .filter((g) => Number.isFinite(g.id))
    .sort((a, b) => {
      const ap = a.parent_gym_id == null ? 0 : 1;
      const bp = b.parent_gym_id == null ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return String(a.nombre).localeCompare(String(b.nombre), 'es');
    });
}

/** Gyms asignables a usuario final (sin plantilla ni ancla plataforma). */
export function gymsForMemberAssignment(gyms = [], currentGymId = null) {
  const list = dedupeGymsById(gyms).filter(
    (g) => isGymActive(g) && !isPlatformTemplateGym(g) && !isD28dPlatformGym(g),
  );
  if (currentGymId == null || currentGymId === '') return list;
  const gid = Number(currentGymId);
  if (!Number.isFinite(gid) || list.some((g) => Number(g.id) === gid)) return list;
  const current = dedupeGymsById(gyms).find((g) => Number(g.id) === gid);
  return current ? [...list, current] : list;
}

/**
 * Alta manual admin_d28d: incluye gym ancla D28D + gyms virtuales + sedes
 * (excluye solo plantilla interna D28D-SEED).
 */
export function gymsForD28dAdminAssignment(gyms = []) {
  const list = dedupeGymsById(gyms).filter(
    (g) => isGymActive(g) && !isPlatformTemplateGym(g),
  );
  const rank = (g) => {
    if (isD28dPlatformGym(g)) return 0;
    if (g.parent_gym_id == null || g.parent_gym_id === '') return 1;
    return 2;
  };
  return [...list].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 2) {
      const pa = Number(a.parent_gym_id) - Number(b.parent_gym_id);
      if (pa !== 0) return pa;
    }
    return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es');
  });
}

/** Gyms virtuales raíz visibles en filtro D28D (sin ancla ni plantilla). */
export function filterableVirtualGymsForD28d(gyms = []) {
  return dedupeGymsById(gyms).filter(
    (g) => (g.parent_gym_id == null || g.parent_gym_id === '')
      && isGymActive(g)
      && !isPlatformTemplateGym(g)
      && !isD28dPlatformGym(g),
  );
}

/** Gyms asignables a hosts D28D (excluye plantilla interna y ancla plataforma). */
export function isGymSelectableForD28dHost(gym) {
  if (!isGymActive(gym)) return false;
  if (isPlatformTemplateGym(gym)) return false;
  if (isD28dPlatformGym(gym)) return false;
  return true;
}

/** Plantilla interna D28D-SEED — no listar en UI operativa de gyms. */
export function isPlatformTemplateGym(gym) {
  if (!gym) return false;
  const invite = String(gym.invite_code || gym.inviteCode || '').toUpperCase();
  if (invite === D28D_SEED_INVITE) return true;
  const slug = String(gym.brand_slug || gym.brandSlug || '').toLowerCase();
  return slug === D28D_SEED_SLUG;
}

/** Gym ancla D28D marca blanca — operativo; admite sedes y gestión. */
export function isD28dPlatformGym(gym) {
  if (!gym) return false;
  const invite = String(gym.invite_code || gym.inviteCode || '').toUpperCase();
  if (invite === D28D_ANCHOR_INVITE) return true;
  const slug = String(gym.brand_slug || gym.brandSlug || '').toLowerCase();
  return slug === D28D_ANCHOR_SLUG;
}

/** Gyms ancla de plataforma (D28D-SEED + marca blanca) — no archivables. */
export function isPlatformSeedGym(gym) {
  return isPlatformTemplateGym(gym) || isD28dPlatformGym(gym);
}

export function isTrainerActive(trainer) {
  return Boolean(trainer && trainer.activo !== false);
}

export function isOrgRowVisible(row, gymsById = {}, trainersById = {}) {
  if (String(row?.status || '').toLowerCase() === 'inactive') return false;
  const type = String(row?.tenant_type || '').toLowerCase();
  if (type === 'gym' && row.gym_id != null) {
    const gym = gymsById[row.gym_id];
    if (!isGymActive(gym)) return false;
  }
  if (type === 'trainer' && row.trainer_id != null) {
    const tr = trainersById[row.trainer_id];
    if (!isTrainerActive(tr)) return false;
  }
  return true;
}

/** Árbol: organización principal → sedes (sin parent_gym_id en UI). */
export function buildGymOrgTree(gyms = []) {
  const active = gyms.filter((g) => isGymActive(g) && !isPlatformTemplateGym(g));
  const roots = active.filter((g) => !g.parent_gym_id);
  return roots.map((root) => ({
    gym: root,
    name: resolveOrganizationBrandName({ entity: root }),
    sedes: active
      .filter((g) => Number(g.parent_gym_id) === Number(root.id))
      .map((sede) => ({
        gym: sede,
        name: resolveOrganizationBrandName({ entity: sede }),
      })),
  }));
}

export function readOrgInitialTab() {
  try {
    const tab = sessionStorage.getItem('orgs.initialTab');
    sessionStorage.removeItem('orgs.initialTab');
    if (['todas', 'gimnasios', 'coaches'].includes(tab)) return tab;
    if (tab === 'convenios') return null;
  } catch { /* ignore */ }
  return 'todas';
}

export function setOrgInitialTab(tab) {
  try {
    sessionStorage.setItem('orgs.initialTab', tab);
  } catch { /* ignore */ }
}
